import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import spacetime from 'spacetime';
import { connect } from 'react-redux';
import { useCallback, useMemo } from 'react';

import { KnmResultInfoTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResultInfoTypeCodesEnum';
import { IKnmAddressTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmAddressTypeSelectOption';
import { IKnmInspectorTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmInspectorTypeSelectOption';
import { IKnmResultInfoTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmResultInfoTypeSelectOption';
import {
  getCatalogKnmAddressTypesSelectOptions,
  getCatalogKnmInspectorTypesSelectOptions,
  getCatalogKnmResultInfoTypesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { KNM_RESULTS } from 'CorePackage/MainModule/routes/routes';
import {
  OBJECTS_RESULTS_TITLE_BLOCK_ID,
  getObjectResultBlockId,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErpObjectSelectOption } from 'KnmErpPackage/MainModule/interfaces/IKnmErpObjectSelectOption';
import { getKnmDataCreated, getKnmFormRef, getKnmStatus } from 'KnmPackage/MainModule/selectors/knmSelectors';
import knmSharedStyles from 'KnmPackage/MainModule/styles/KnmShared.module.scss';
import { ResultsOpenedFlagsType } from 'KnmPackage/MainModule/types/ResultsOpenedFlagsType';
import { Button } from 'UIPackage/MainModule/components/Button';
import { tzMoscow } from 'CorePackage/MainModule/constants/timeZone';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsProsecutor, getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { KnmErpStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';

import { IKnmErpFormResult } from '../interfaces/IKnmErpFormResult';
import { KnmErpResult } from './KnmErpResult';

interface IKnmErpResultsProps {
  error?: string;
  knmObjectsOptions: IKnmErpObjectSelectOption[] | undefined;
  objectsResults: IKnmForm['objectsResults'];
  resultsOpenedFlags: ResultsOpenedFlagsType;
  watchOnly: boolean;
  startDate: string | undefined;
  changeResultsOpenedFlags: (id: string, value: boolean) => void;
}

interface IKnmErpResultsStoreProps {
  formRef: IKnmFormRef | undefined;
  knmAddressTypesSelectOptions: IKnmAddressTypeSelectOption[];
  knmInspectorTypesSelectOptions: IKnmInspectorTypeSelectOption[] | undefined;
  knmResultInfoTypesSelectOptions: IKnmResultInfoTypeSelectOption[];
  isUserSupervisor: boolean | undefined;
  isUserProsecutor: boolean | undefined;
  settings: IAdminSetting[] | undefined;
  created: string | null | undefined;
  knmStatusType: IKnmStatus | undefined;
}

const KnmErpResultsWithoutStore = ({
  error,
  knmObjectsOptions,
  objectsResults,
  resultsOpenedFlags,
  watchOnly,
  startDate,
  changeResultsOpenedFlags,
  formRef,
  knmAddressTypesSelectOptions,
  knmInspectorTypesSelectOptions,
  knmResultInfoTypesSelectOptions,
  isUserSupervisor,
  isUserProsecutor,
  settings,
  created,
  knmStatusType,
}: IKnmErpResultsProps & IKnmErpResultsStoreProps) => {
  const userDate = spacetime.now(tzMoscow);
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addKnmResult = useCallback(
    () => !watchOnly && push && push('objectsResults', { listId: uuidv4(), isNew: true }),
    [push, watchOnly],
  );
  const makeRemoveKnmResultHandler = (index: number) => () => !watchOnly && remove && remove('objectsResults', index);

  const isSomeAdditionalInfoImpossibleReasonValid = objectsResults?.some(objectResult => {
    const additionalInfoImpossibleReason = objectResult.additionalInfo?.find(
      additionalInfoItem => additionalInfoItem?.type?.code === KnmResultInfoTypeCodesEnum.UNIMPOSSIBLE_REASON_I,
    );

    if (additionalInfoImpossibleReason?.information) {
      return true;
    }

    return false;
  });

  const isChangeResultsAvailableProsecutorCase = useMemo(() => {
    return (
      (isUserProsecutor && knmStatusType?.code !== KnmErpStatusCodesEnum.TYPE_COMPLETED) ||
      knmStatusType?.code !== KnmErpStatusCodesEnum.TYPE_CANT_BE_CARRY_OUT
    );
  }, [isUserProsecutor, knmStatusType]);

  const isResultsEditAvailable = !watchOnly || isChangeResultsAvailableProsecutorCase;

  const filteredKnmResultInfoTypesSelectOptions:
    | IKnmResultInfoTypeSelectOption[]
    | undefined = knmResultInfoTypesSelectOptions?.filter(
    knmResultInfoTypeSelectOption => knmResultInfoTypeSelectOption.code !== KnmResultInfoTypeCodesEnum.UNDOIG_SEC_I,
  );

  return (
    <Section
      title="Список результатов"
      id={KNM_RESULTS}
      titleId={OBJECTS_RESULTS_TITLE_BLOCK_ID}
      type="list"
      customButton={
        <Button
          className="shared-btn-add"
          text="Добавить"
          type="button"
          variant="link"
          color="primary"
          disabled={
            watchOnly ||
            Boolean(error) ||
            isSomeAdditionalInfoImpossibleReasonValid ||
            !startDate ||
            userDate.isBefore(
              spacetime(formatDate({ date: startDate || '', dateFormat: SYSTEM_DATE_FORMAT_FNS }) || ''),
            )
          }
          onClick={addKnmResult}
        />
      }
    >
      {error && <p className={knmSharedStyles.SectionErrorNotification}>{error}</p>}

      <FieldArray name="objectsResults">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IKnmErpFormResult = fields.value?.[index];
            const id = getObjectResultBlockId(fieldValue.listId);

            if (!fieldValue.listId) {
              return null;
            }

            return (
              <KnmErpResult
                key={id}
                formRef={formRef}
                id={id}
                index={index}
                knmObjectsOptions={knmObjectsOptions}
                knmAddressTypesOptions={knmAddressTypesSelectOptions}
                knmInspectorTypesSelectOptions={knmInspectorTypesSelectOptions}
                knmResultInfoTypesSelectOptions={filteredKnmResultInfoTypesSelectOptions}
                name={name}
                objectsResults={objectsResults}
                resultsOpenedFlags={resultsOpenedFlags}
                value={fieldValue}
                watchOnly={!isResultsEditAvailable}
                changeResultsOpenedFlags={changeResultsOpenedFlags}
                onRemove={makeRemoveKnmResultHandler(index)}
                isUserSupervisor={isUserSupervisor}
                isUserProsecutor={isUserProsecutor}
                settings={settings}
                created={created}
              />
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IKnmErpResultsStoreProps => ({
  formRef: getKnmFormRef(state),
  knmAddressTypesSelectOptions: getCatalogKnmAddressTypesSelectOptions(state),
  knmInspectorTypesSelectOptions: getCatalogKnmInspectorTypesSelectOptions(state),
  knmResultInfoTypesSelectOptions: getCatalogKnmResultInfoTypesSelectOptions(state),
  isUserSupervisor: getUserRoleIsSupervisor(state),
  isUserProsecutor: getUserRoleIsProsecutor(state),
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
  knmStatusType: getKnmStatus(state),
});

export const KnmErpResults = connect(mapStateToProps)(KnmErpResultsWithoutStore);
